import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import { action } from 'mobx';
import { Appl } from '../../../../core/Appl';
import { View, Button, ButtonType, Input, InputType, InputCalendar, InputDropdown } from '../../../../core/components/Index';
import CaseStatusViewModel from './CaseStatusViewModel';
import { MemberCaseStatusDto, CaseStepSummary, PIPELINE_OPTIONS } from './CaseStatusModel';
import { CheckCircle2, Clock3, Circle, Briefcase, FileText, ArrowLeftRight, Shield, CalendarDays, Hash } from 'lucide-react';
import { formatDate } from '../../../../core/services/Helper';

// ─── Pipeline config ──────────────────────────────────────────────────────────

const PIPELINE_CONFIG: Record<string, { label: string; icon: React.ReactNode; accent: string; light: string; border: string }> = {
  RETIREMENT: {
    label: 'Retirement',
    icon: <Briefcase className="w-4 h-4" />,
    accent: 'text-primary-700',
    light: 'bg-primary-50 border-primary-200',
    border: 'border-primary-400',
  },
  TERMINATION: {
    label: 'Termination',
    icon: <FileText className="w-4 h-4" />,
    accent: 'text-orange-700',
    light: 'bg-orange-50 border-orange-200',
    border: 'border-orange-400',
  },
  RECIPROCITY: {
    label: 'Reciprocity',
    icon: <ArrowLeftRight className="w-4 h-4" />,
    accent: 'text-blue-700',
    light: 'bg-blue-50 border-blue-200',
    border: 'border-blue-400',
  },
};

const getPipelineConfig = (type: string) =>
  PIPELINE_CONFIG[type] ?? { label: type, icon: <FileText className="w-4 h-4" />, accent: 'text-gray-700', light: 'bg-gray-50 border-gray-200', border: 'border-gray-400' };

// ─── Step timeline ─────────────────────────────────────────────────────────────

const StepRow = ({ step, isLast }: { step: CaseStepSummary; isLast: boolean }) => {
  const isCompleted = step.status === 'COMPLETED';
  const isCurrent = step.status === 'CURRENT';

  return (
    <div className="flex gap-4">
      {/* Icon + connector line */}
      <div className="flex flex-col items-center">
        <div className={`
          w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 z-10
          transition-all duration-300
          ${isCompleted ? 'bg-primary-500 shadow-md shadow-primary-200' : ''}
          ${isCurrent ? 'bg-orange-500 shadow-md shadow-orange-200 ring-4 ring-orange-100' : ''}
          ${!isCompleted && !isCurrent ? 'bg-white border-2 border-gray-200' : ''}
        `}>
          {isCompleted && <CheckCircle2 className="w-5 h-5 text-white" />}
          {isCurrent && <Clock3 className="w-5 h-5 text-white" />}
          {!isCompleted && !isCurrent && <Circle className="w-4 h-4 text-gray-300" />}
        </div>
        {!isLast && (
          <div className={`w-0.5 flex-1 mt-1 mb-1 min-h-[28px] ${isCompleted ? 'bg-primary-300' : 'bg-gray-200'}`} />
        )}
      </div>

      {/* Step content */}
      <div className={`flex-1 pb-5 ${isLast ? 'pb-0' : ''}`}>
        <div className={`
          rounded-xl px-4 py-3 border transition-all
          ${isCompleted ? 'bg-primary-50 border-primary-100' : ''}
          ${isCurrent ? 'bg-orange-50 border-orange-200 shadow-sm' : ''}
          ${!isCompleted && !isCurrent ? 'bg-gray-50 border-gray-100' : ''}
        `}>
          <div className="flex items-center justify-between">
            <span className={`text-sm font-semibold
              ${isCompleted ? 'text-primary-700' : ''}
              ${isCurrent ? 'text-orange-700' : ''}
              ${!isCompleted && !isCurrent ? 'text-gray-400' : ''}
            `}>
              {step.stepName}
            </span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full
              ${isCompleted ? 'bg-primary-100 text-primary-700' : ''}
              ${isCurrent ? 'bg-orange-100 text-orange-700' : ''}
              ${!isCompleted && !isCurrent ? 'bg-gray-200 text-gray-500' : ''}
            `}>
              {isCompleted ? 'Completed' : isCurrent ? 'In Progress' : 'Pending'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Case card ─────────────────────────────────────────────────────────────────

const CaseCard = ({ caseStatus }: { caseStatus: MemberCaseStatusDto }) => {
  const cfg = getPipelineConfig(caseStatus.pipelineType);
  const pct = caseStatus.progressPercent;

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 mb-8">

      {/* Header band */}
      <div className="bg-gradient-to-r from-primary-700 via-primary-600 to-primary-500 px-7 py-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-primary-200 text-xs font-medium uppercase tracking-widest mb-1">
              Member Case
            </p>
            <h2 className="text-white text-2xl font-bold leading-tight">
              {caseStatus.memberName}
            </h2>
            <div className="flex items-center gap-3 mt-2">
              {/* Pipeline badge */}
              <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1 rounded-full border border-white/20">
                {cfg.icon}
                {cfg.label}
              </span>
              <span className="text-primary-200 text-xs flex items-center gap-1">
                <Hash className="w-3 h-3" />
                {caseStatus.caseReference}
              </span>
            </div>
          </div>
          {/* Status pill */}
          <div className="text-right flex-shrink-0">
            <span className="inline-block bg-white text-primary-700 text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wide shadow-sm">
              {caseStatus.overallStatus.replace(/_/g, ' ')}
            </span>
          </div>
        </div>
      </div>

      {/* Progress section */}
      <div className="px-7 py-5 border-b border-gray-100 bg-gradient-to-b from-gray-50 to-white">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-gray-600">Overall Progress</span>
          <span className="text-2xl font-bold text-primary-600">{pct}%</span>
        </div>
        {/* Segmented progress bar */}
        <div className="relative">
          <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
            <div
              className="h-4 rounded-full bg-gradient-to-r from-primary-600 to-primary-400 transition-all duration-700 ease-out"
              style={{ width: `${Math.max(pct, 4)}%` }}
            />
          </div>
          {/* Step tick marks */}
          <div className="absolute inset-0 flex items-center">
            {Array.from({ length: caseStatus.totalSteps - 1 }).map((_, i) => (
              <div
                key={i}
                className="absolute w-0.5 h-4 bg-white/60"
                style={{ left: `${((i + 1) / caseStatus.totalSteps) * 100}%` }}
              />
            ))}
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-1.5">
          {caseStatus.completedSteps} of {caseStatus.totalSteps} steps completed
        </p>
      </div>

      {/* Steps timeline */}
      <div className="px-7 py-6">
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-5">
          Case Timeline
        </h3>
        <div>
          {caseStatus.steps.map((step, idx) => (
            <StepRow
              key={step.stepCode || idx}
              step={step}
              isLast={idx === caseStatus.steps.length - 1}
            />
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-100 bg-gray-50 px-7 py-4 flex flex-wrap gap-6">
        <span className="flex items-center gap-2 text-xs text-gray-500">
          <CalendarDays className="w-3.5 h-3.5 text-gray-400" />
          Started <strong className="text-gray-700 font-semibold">{formatDate(caseStatus.startDate)}</strong>
        </span>
        {caseStatus.dueDate && (
          <span className="flex items-center gap-2 text-xs text-gray-500">
            <Clock3 className="w-3.5 h-3.5 text-gray-400" />
            Est. Completion <strong className="text-gray-700 font-semibold">{formatDate(caseStatus.dueDate)}</strong>
          </span>
        )}
      </div>
    </div>
  );
};

// ─── Hero banner (lookup page) ─────────────────────────────────────────────────

const HeroBanner = () => (
  <div className="bg-gradient-to-br from-primary-800 via-primary-700 to-primary-600 rounded-2xl px-8 py-8 mb-8 text-white relative overflow-hidden">
    {/* Decorative circles */}
    <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/5" />
    <div className="absolute -bottom-10 -left-6 w-32 h-32 rounded-full bg-white/5" />

    <div className="relative">
      <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 text-xs font-semibold text-primary-100 mb-3 border border-white/15">
        <Shield className="w-3.5 h-3.5" />
        Secure Member Access
      </div>
      <h2 className="text-2xl font-bold text-white mb-1 leading-snug">
        Track Your Case Progress
      </h2>
      <p className="text-primary-200 text-sm leading-relaxed max-w-sm">
        Enter your Member ID and date of birth to see a real-time view of your case status and next steps.
      </p>
    </div>
  </div>
);

// ─── Main view ─────────────────────────────────────────────────────────────────

const CaseStatusView = observer(() => {
  const vm = Appl.Services.get<CaseStatusViewModel>('CaseStatusViewModel');
  vm.navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await vm.lookupStatusAsync();
  };

  return (
    <View id="CaseStatusView" pageTitle="Case Status" showMenuBar={false}>
      <div className="max-w-2xl mx-auto px-4 pt-6 pb-16">

        {/* ── Lookup form ── */}
        {vm.caseList.length === 0 && (
          <>
            <HeroBanner />

            <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
              {/* Form header */}
              <div className="px-8 pt-7 pb-4 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-800">Find Your Case</h3>
                <p className="text-sm text-gray-500 mt-0.5">All fields marked with * are required</p>
              </div>

              <form onSubmit={handleSubmit} className="px-8 py-6 space-y-5">
                <Input
                  id="pin"
                  label="Member ID (PIN) *"
                  value={vm.pin}
                  inputType={InputType.Text}
                  onChange={(e: any) => vm.onInputChange(e, 'pin', vm)}
                  placeholder="Enter your 6-digit Member ID"
                  required={true}
                  maxLength={20}
                />
                <InputCalendar
                  id="dateOfBirth"
                  label="Date of Birth *"
                  value={vm.dateOfBirth as any}
                  onChange={(date: any) => action(() => { vm.dateOfBirth = date ?? undefined; })()}
                  required={true}
                />
                <InputDropdown
                  id="pipelineType"
                  label="Case Type"
                  value={vm.pipelineType || null}
                  options={PIPELINE_OPTIONS}
                  onChange={(e: any) => action(() => { vm.pipelineType = e.value ?? ''; })()}
                  placeholder="All Cases (optional)"
                  showClear={!!vm.pipelineType}
                />

                {vm.errorMessage && !vm.hasSearched && (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                    <span className="text-red-500 mt-0.5">⚠</span>
                    <p className="text-red-700 text-sm">{vm.errorMessage}</p>
                  </div>
                )}

                <div className="pt-1">
                  <Button
                    id="btnLookup"
                    label={vm.isLoading ? 'Searching…' : 'Check My Status'}
                    buttonType={ButtonType.Submit}
                    disabled={vm.isLoading}
                    className="w-full success"
                  />
                </div>
              </form>

              {/* Privacy note */}
              <div className="px-8 pb-6 flex items-center gap-2 text-xs text-gray-400">
                <Shield className="w-3.5 h-3.5 flex-shrink-0" />
                Your information is used solely to display your case status and is never stored by this form.
              </div>
            </div>
          </>
        )}

        {/* ── Results ── */}
        {vm.hasSearched && (
          <div className="animate-slideDownAndFade">
            {vm.errorMessage ? (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
                <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Clock3 className="w-7 h-7 text-amber-500" />
                </div>
                <h3 className="text-base font-semibold text-amber-800 mb-1">No Case Found</h3>
                <p className="text-sm text-amber-700">{vm.errorMessage}</p>
              </div>
            ) : (
              <>
                {/* Greeting banner */}
                <div className="bg-gradient-to-r from-primary-700 to-primary-500 rounded-2xl px-7 py-5 mb-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xl font-bold">
                      {vm.caseList[0]?.memberName?.charAt(0) ?? '?'}
                    </span>
                  </div>
                  <div>
                    <p className="text-primary-200 text-xs font-medium uppercase tracking-widest">Welcome back</p>
                    <p className="text-white text-xl font-bold">{vm.caseList[0]?.memberName}</p>
                    <p className="text-primary-200 text-xs mt-0.5">
                      {vm.caseList.length} active case{vm.caseList.length !== 1 ? 's' : ''} found
                    </p>
                  </div>
                </div>

                {vm.caseList.map((c) => (
                  <CaseCard key={`${c.pipelineType}-${c.caseReference}`} caseStatus={c} />
                ))}
              </>
            )}

            <div className="text-center mt-6">
              <button
                className="inline-flex items-center gap-2 text-sm text-primary-600 font-semibold hover:text-primary-800 underline underline-offset-4 transition-colors"
                onClick={() => vm.reset()}
              >
                ← Check another member
              </button>
            </div>
          </div>
        )}
      </div>
    </View>
  );
});

export default CaseStatusView;
